import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';

// Configuração do Stripe
const stripePromise = loadStripe('pk_test_51RSF9sGqkSLXk22PO5KdxmcpZDlTCVNbeuesNStgLRYLvpjIiLiyc07VsslVgGluHUvREXAgGzZ6sjfpK8PhEhS100JATbCafZ');

// Cores da Sidebar
const colors = {
  primary: '#5AB9D3',
  primaryLight: '#6AC9E3',
  primaryDark: '#4AA5C0',
  secondary: '#101A3A',
  secondaryLight: '#1A2747',
  white: '#FFFFFF',
  textLight: '#E2E8F0',
  accent: '#38A169',
  error: '#E53E3E',
  success: '#38A169'
};

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0px); }
`;

const PremiumCheckout = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const buildApiUrl = (endpoint) => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8087';
    return `${baseUrl.replace(/\/+$/, '')}/${endpoint.replace(/^\/+/, '')}`;
  };

  const handleCheckout = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      if (!user.token) {
        throw new Error('Usuário não autenticado');
      }

      if (!user.email) {
        throw new Error('Email do usuário não disponível');
      }

      const stripe = await stripePromise;
      
      const url = new URL(buildApiUrl('payments/checkout-session'));
      url.searchParams.append('customerEmail', user.email);
      
      const response = await axios.post(url.toString(), null, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`,
        },
      });

      if (!response.data || !response.data.sessionId) {
        throw new Error('Resposta inválida do servidor');
      }

      const { sessionId } = response.data;

      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId: sessionId
      });

      if (stripeError) {
        throw stripeError;
      }

    } catch (err) {
      console.error('Erro no checkout:', err);
      setError(err.response?.data?.message || 
              err.message || 
              'Erro ao processar o pagamento. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <CheckoutContainer>
      <PremiumCard>
        <PlanHeader>
          <PremiumBadge>
            <BadgeRibbon />
            <span>PLANO PREMIUM</span>
          </PremiumBadge>
          <PlanName>Professional</PlanName>
          <PlanPrice>
            <PriceCurrency>€</PriceCurrency>
            <PriceAmount>19</PriceAmount>
            <PricePeriod>/mês</PricePeriod>
          </PlanPrice>
          <PlanDescription>Acesso completo às funcionalidades exclusivas</PlanDescription>
        </PlanHeader>
        
        <Divider />
        
        <PlanFeatures>
          <FeatureItem>
            <FeatureIcon>✓</FeatureIcon>
            <FeatureText>
              <strong>Destaque Premium</strong> - Posicionamento privilegiado em todas as buscas
            </FeatureText>
          </FeatureItem>
          <FeatureItem>
            <FeatureIcon>✓</FeatureIcon>
            <FeatureText>
              <strong>Leads Ilimitados</strong> - Acesso direto a clientes qualificados
            </FeatureText>
          </FeatureItem>
          <FeatureItem>
            <FeatureIcon>✓</FeatureIcon>
            <FeatureText>
              <strong>Analytics Avançados</strong> - Relatórios estratégicos completos
            </FeatureText>
          </FeatureItem>
          <FeatureItem>
            <FeatureIcon>✓</FeatureIcon>
            <FeatureText>
              <strong>Suporte Prioritário</strong> - Atendimento dedicado 24/7
            </FeatureText>
          </FeatureItem>
        </PlanFeatures>
        
        <CheckoutButton 
          onClick={handleCheckout}
          disabled={loading}
          aria-busy={loading}
        >
          {loading ? (
            <>
              <Spinner />
              Processando...
            </>
          ) : (
            'Assinar Agora'
          )}
        </CheckoutButton>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <SecuritySection>
          <SecurityBadge>
            <LockIcon />
            <SecurityText>Pagamento 100% seguro processado pela Stripe</SecurityText>
          </SecurityBadge>
          <GuaranteeBadge>
            <GuaranteeIcon>✓</GuaranteeIcon>
            <GuaranteeText>Garantia de satisfação de 7 dias</GuaranteeText>
          </GuaranteeBadge>
        </SecuritySection>
      </PremiumCard>
    </CheckoutContainer>
  );
};

// Estilos com Styled Components
const CheckoutContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 2rem;
  background: #ffffff;
`;

const PremiumCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  padding: 3rem;
  max-width: 500px;
  width: 100%;
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.6s ease-out;
  border: 1px solid rgba(0, 0, 0, 0.05);
`;

const PlanHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  position: relative;
`;

const PremiumBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background: linear-gradient(90deg, ${colors.primary} 0%, ${colors.primaryDark} 100%);
  color: ${colors.white};
  padding: 0.6rem 1.8rem;
  border-radius: 0 50px 50px 0;
  font-weight: 700;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 2rem;
  box-shadow: 0 4px 15px rgba(90, 185, 211, 0.3);
  position: relative;
  left: -3.5rem;
  
  span {
    position: relative;
    z-index: 1;
  }
`;

const BadgeRibbon = styled.div`
  position: absolute;
  left: -8px;
  top: 0;
  width: 0;
  height: 0;
  border-right: 8px solid ${colors.primaryDark};
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
`;

const PlanName = styled.h2`
  color: #2d3748;
  margin: 0;
  font-size: 2.2rem;
  font-weight: 700;
  line-height: 1.2;
`;

const PlanPrice = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  margin: 1.5rem 0;
  animation: ${float} 4s ease-in-out infinite;
`;

const PriceCurrency = styled.span`
  color: ${colors.primary};
  font-size: 1.8rem;
  font-weight: 600;
  margin-right: 0.3rem;
  line-height: 1;
  align-self: flex-start;
`;

const PriceAmount = styled.span`
  color: ${colors.primary};
  font-size: 3.2rem;
  font-weight: 800;
  line-height: 1;
`;

const PricePeriod = styled.span`
  color: #718096;
  font-size: 1.2rem;
  font-weight: 500;
  margin-left: 0.5rem;
  line-height: 1.5;
`;

const PlanDescription = styled.p`
  color: #718096;
  font-size: 1.1rem;
  margin: 1rem 0 0;
  font-weight: 500;
  line-height: 1.5;
`;

const Divider = styled.div`
  height: 1px;
  background: linear-gradient(90deg, rgba(90, 185, 211, 0.1) 0%, ${colors.primary} 50%, rgba(90, 185, 211, 0.1) 100%);
  margin: 2rem 0;
`;

const PlanFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 3rem;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  padding: 1rem 0;
  animation: ${fadeIn} 0.5s ease-out;
  animation-fill-mode: both;

  &:nth-child(1) { animation-delay: 0.1s; }
  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.3s; }
  &:nth-child(4) { animation-delay: 0.4s; }
`;

const FeatureIcon = styled.span`
  color: ${colors.primary};
  font-weight: bold;
  font-size: 1.3rem;
  margin-right: 1.2rem;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(90, 185, 211, 0.1);
  border-radius: 50%;
  flex-shrink: 0;
`;

const FeatureText = styled.span`
  color: #4a5568;
  font-size: 1.05rem;
  line-height: 1.6;
  
  strong {
    color: #2d3748;
    font-weight: 600;
  }
`;

const CheckoutButton = styled.button`
  width: 100%;
  padding: 1.25rem;
  background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%);
  color: ${colors.white};
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(90, 185, 211, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(90, 185, 211, 0.4);
    background: linear-gradient(135deg, ${colors.primaryDark} 0%, ${colors.primary} 100%);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    box-shadow: 0 4px 15px rgba(90, 185, 211, 0.3);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: 0.5s;
  }

  &:hover::before {
    left: 100%;
  }
`;

const Spinner = styled.div`
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top: 2px solid ${colors.white};
  width: 1.2rem;
  height: 1.2rem;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.p`
  color: ${colors.error};
  font-size: 1rem;
  margin: 1.5rem 0 0;
  text-align: center;
  padding: 1.2rem;
  background-color: rgba(229, 62, 62, 0.1);
  border-radius: 10px;
  border-left: 4px solid ${colors.error};
  animation: ${fadeIn} 0.3s ease-out;
`;

const SecuritySection = styled.div`
  margin-top: 2.5rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
`;

const SecurityBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
`;

const LockIcon = styled.div`
  color: ${colors.primary};
  margin-right: 0.8rem;
  font-size: 1.3rem;
`;

const SecurityText = styled.span`
  color: #718096;
  font-size: 0.9rem;
  font-weight: 500;
`;

const GuaranteeBadge = styled.div`
  display: flex;
  align-items: center;
  background: rgba(56, 161, 105, 0.1);
  border-radius: 10px;
  padding: 1.2rem;
  border: 1px solid rgba(56, 161, 105, 0.2);
`;

const GuaranteeIcon = styled.div`
  width: 28px;
  height: 28px;
  background: ${colors.accent};
  color: ${colors.white};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 1rem;
  flex-shrink: 0;
  font-size: 1.1rem;
`;

const GuaranteeText = styled.div`
  color: ${colors.accent};
  font-size: 0.95rem;
  font-weight: 600;
`;

export default PremiumCheckout;